import { computePosition, autoUpdate } from "@floating-ui/dom";
import { TActivityType, TActivityTypeFilter } from "~/interfaces";
import { findFirstFocusableElement, node } from "~/util/dom";
import createSDSIcon from "../icon";
import { activityTypeToIcon, activityTypeToName } from "~/activities/util";
import { createSelectItem } from "./selectItem";
import { _ } from "~/i18n";
import { EMPTY_SYMBOL_24, CHEVRON_UP_24, CHEVRON_DOWN_24 } from "@sznds/icons";

interface IActivitySelectOptions {
	selected: TActivityTypeFilter;
	onChange: (type: TActivityTypeFilter) => void;
	options: Array<TActivityType>;
}

export default class ActivitySelect {
	#button: HTMLButtonElement;

	#popover: HTMLDivElement;

	#selectedText: HTMLDivElement;

	#onChange: IActivitySelectOptions["onChange"];

	#selectedType: IActivitySelectOptions["selected"];

	#cleanup: ReturnType<typeof autoUpdate>;

	#popOverVisible: boolean;

	constructor({
		selected = "auto_moto",
		onChange,
		options,
	}: Partial<IActivitySelectOptions>) {
		this.#onChange = onChange;
		this.#selectedType = selected;
		this.#popOverVisible = false;
		this.#button = node("button", { type: "button", className: "ui-activity-select" });
		this.#popover = node("div", { className: "ui-activity-select__popover" });
		this.#selectedText = node("div", { className: "ui-activity-select__selected" });

		// inicializujeme vizual tlacitka selectu
		this.#button.append(createSDSIcon({ symbol: EMPTY_SYMBOL_24 }), this.#selectedText, createSDSIcon({ symbol: CHEVRON_DOWN_24 }));
		this.#setSelected(this.#selectedType);
		this.#button.addEventListener("click", this);

		// sestavime obsah popoveru
		const container = node("div", { className: "ui-activity-select__container" });

		// mame sestavit jen par typu do filtru
		if (Array.isArray(options)) {
			const filter = node("div", { className: "ui-activity-select__section" });

			[
				"all",
				...options,
			].forEach((type: TActivityTypeFilter) => {
				const item = createSelectItem(type);

				if (type === "all") {
					item.append(node("span", {}, _("activity.type.all")));
				} else {
					item.append(createSDSIcon({ symbol: activityTypeToIcon(type) }), node("span", {}, activityTypeToName(type)));
				}

				item.addEventListener("click", (event: PointerEvent) => {
					if (event.pointerType !== "" && event.target === item) {
						this.#setSelected(type);
						this.#onChange(type);
						this.#hidePopover();
					}
				});
				item.addEventListener("keypress", event => {
					if (event.code === "Enter") {
						event.stopImmediatePropagation();
						this.#setSelected(type);
						this.#onChange(type);
						this.#hidePopover();
						this.#button.focus();
					}
				});

				filter.appendChild(item);
			});

			container.appendChild(filter);
			this.#popover.appendChild(container);
		// nedostali jsme seznam typu, tak buildime vyber ze vsech existujicich
		} else {
			const types: Array<TActivityType> = Mapy.CUS.userweb.moreActivityTypes2
				? [
					"auto_moto",
					"karavan",
					"motorka",
					"pesky",
					"horolezectvi",
					"beh",
					"ferrata",
					"kocarek",
					"inline",
					"gravel",
					"horske_kolo",
					"silnicni_kolo",
					"nakole",
					"ekolo",
					"kolobezka",
					"jezdectvi",
					"bezky",
					"sjezd",
					"sneznice",
					"skialpy",
					"brusle",
					"vodactvi",
					"paddleboard",
					"letadlo",
					"paraglide",
				]
				: [
					"auto_moto",
					"karavan",
					"motorka",
					"pesky",
					"horolezectvi",
					"beh",
					"kocarek",
					"inline",
					"gravel",
					"horske_kolo",
					"silnicni_kolo",
					"nakole",
					"kolobezka",
					"jezdectvi",
					"bezky",
					"sjezd",
					"sneznice",
					"skialpy",
					"vodactvi",
					"paddleboard",
					"letadlo",
					"paraglide",
				];
			const items = types.map((type: TActivityType) => {
				const item = createSelectItem(type);

				item.append(createSDSIcon({ symbol: activityTypeToIcon(type) }), node("span", {}, activityTypeToName(type)));
				item.addEventListener("click", (event: PointerEvent) => {
					if (event.pointerType !== "" && event.target === item) {
						this.#setSelected(type);
						this.#onChange(type);
						this.#hidePopover();
					}
				});
				item.addEventListener("keypress", event => {
					if (event.code === "Enter") {
						event.stopImmediatePropagation();
						this.#setSelected(type);
						this.#onChange(type);
						this.#hidePopover();
						this.#button.focus();
					}
				});

				return item;
			});

			const motorism = node("div", { className: "ui-activity-select__section" });
			const pedestrian = node("div", { className: "ui-activity-select__section" });
			const saddle = node("div", { className: "ui-activity-select__section" });
			const winter = node("div", { className: "ui-activity-select__section" });
			const water = node("div", { className: "ui-activity-select__section" });
			const air = node("div", { className: "ui-activity-select__section" });

			if (Mapy.CUS.userweb.moreActivityTypes2) {
				motorism.append(...items.slice(0, 3));
				pedestrian.append(...items.slice(3, 9));
				saddle.append(...items.slice(9, 16));
				winter.append(...items.slice(16, 21));
				water.append(...items.slice(21, 23));
				air.append(...items.slice(23, 25));
			} else {
				motorism.append(...items.slice(0, 3));
				pedestrian.append(...items.slice(3, 8));
				saddle.append(...items.slice(8, 14));
				winter.append(...items.slice(14, 18));
				water.append(...items.slice(18, 20));
				air.append(...items.slice(20, 22));
			}

			container.append(
				// motorismus
				node("h2", {}, _("activity.type.title.motorism")),
				motorism,
				// pesi
				node("h2", {}, _("activity.type.title.pedestrian")),
				pedestrian,
				// v sedle
				node("h2", {}, _("activity.type.title.saddle")),
				saddle,
				// zimni sporty
				node("h2", {}, _("activity.type.title.winter")),
				winter,
				// vodni sporty
				node("h2", {}, _("activity.type.title.water")),
				water,
				// vzdusne sporty
				node("h2", {}, _("activity.type.title.air")),
				air,
			);
			this.#popover.appendChild(container);
		}
	}

	destroy() {
		this.#hidePopover();
		this.#button.removeEventListener("click", this);
	}

	get container() {
		return this.#button;
	}

	#setSelected(selected: TActivityTypeFilter) {
		this.#selectedType = selected;
		this.#selectedText.innerHTML = "";

		if (selected === "all") {
			this.#selectedText.append(node("span", {}, _("activity.type.all")));
		} else {
			this.#selectedText.append(createSDSIcon({ symbol: activityTypeToIcon(selected) }), node("span", {}, activityTypeToName(selected)));
		}
	}

	set selected(selected: TActivityTypeFilter) {
		this.#setSelected(selected);
	}

	#hidePopover() {
		if (!this.#popOverVisible) {
			return;
		}
		this.#popover.remove();
		document.body.removeEventListener("keydown", this);
		document.body.removeEventListener("pointerdown", this);
		this.#cleanup?.();
		this.#button.lastElementChild.replaceWith(createSDSIcon({ symbol: CHEVRON_DOWN_24 }));
		this.#popOverVisible = false;
	}

	#showPopover() {
		if (this.#popOverVisible) {
			return;
		}

		document.body.append(this.#popover);
		document.body.addEventListener("keydown", this);
		document.body.addEventListener("pointerdown", this);
		this.#cleanup = autoUpdate(this.#button, this.#popover, () => {
			computePosition(this.#button, this.#popover, {
				placement: "bottom-start",
				strategy: "fixed",
			}).then(({ x, y }) => {
				Object.assign(this.#popover.style, {
					left: `${x}px`,
					top: `${y}px`,
					width: `${this.#button.clientWidth}px`,
					maxHeight: `${window.innerHeight - y - 8}px`,
				});

				Array.from(this.#popover.querySelectorAll("input[type=radio]")).forEach((input: HTMLInputElement) => {
					input.checked = input.value === this.#selectedType;
				});

				const checked = this.#popover.querySelector("input:checked") as HTMLInputElement;

				if (checked) {
					checked.focus();
				} else {
					findFirstFocusableElement(this.#popover)?.focus();
				}
			});
		});
		this.#button.lastElementChild.replaceWith(createSDSIcon({ symbol: CHEVRON_UP_24 }));
		this.#popOverVisible = true;
	}

	handleEvent(event: KeyboardEvent | MouseEvent) {
		if (event.type === "click") {
			if (this.#button.contains(event.target as Node)) {
				if (this.#popOverVisible) {
					this.#hidePopover();
				} else {
					this.#showPopover();
				}
			}
		} else if (event.type === "pointerdown") {
			if (!this.#popover.contains(event.target as Node) && !this.#button.contains(event.target as Node)) {
				this.#hidePopover();
			}
		} else if ((event as KeyboardEvent).code === "Escape") {
			event.stopImmediatePropagation();
			this.#hidePopover();
			this.#button.focus();
		}
	}
}
